import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Skeleton, Tooltip } from '@patternfly/react-core';
import InsightsActions from './InsightsActions';
import { Widget } from '../../types/dashboards/dashboard';
import { InsightsActionsResponse } from '../../api/types';
import {
	faSparkles,
	faTimes,
	faExpand,
	faCompass,
	faChevronUp,
	faChevronDown,
	faArrowsRotate,
} from '@fortawesome/pro-regular-svg-icons';
import { TDashboardInightsEntity } from '../../api/shared-entity/SharedEntity';
import { InsightsActions as InsightsActionsAPI } from '../../api/lambda/InsightsActions';
import {
	InsightsFilterContext,
	LambdaDashboardSummaryRequest,
	LambdaGraphSummariesRequest,
	StatSummary,
} from '../../api/types/lambda-graph-summaries-request';
import { TDashboard } from '../../api/dashbboards/Dashboards';
import InsightsChangeNotice from './InsightsChangeNotice';
import { useNoticeAnchors } from '../../hooks/NoticeAnchorHooks';
import { useCommonStoreContext } from '../common-store/CommonStoreProvider';
import { useApplication, useIsGranted } from '../user/ApplicationProvider';
import { getExtendedPrompt } from '../../api/zone/getExtendedPrompt';
import { LambdaGraphSummariesResponse } from '../../api/types/lambda-graph-summaries-response';
import ChatInterface, { ChatMessages, Message } from './ChatInterface';
import { Permission } from '../../enums/permission.enum';
import DiscoverQuestionsModal from './DiscoverQuestionsModal';
import { AxiosError } from 'axios';
import { useParams } from 'react-router-dom';
import { widgetGraphId } from '../../helpers/insights-statistics-calculator';

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const retryWithBackoff = async <T,>(
	fn: () => Promise<T>,
	maxRetries = 3,
	baseDelayMs = 1000
): Promise<T> => {
	let lastError: Error | null = null;
	for (let attempt = 0; attempt < maxRetries; attempt++) {
		try {
			return await fn();
		} catch (error) {
			lastError = error as Error;
			if (attempt < maxRetries - 1) {
				const delayMs = baseDelayMs * Math.pow(2, attempt);
				console.log(`Retry attempt ${attempt + 1}/${maxRetries} after ${delayMs}ms`);
				await delay(delayMs);
			}
		}
	}
	throw lastError;
};

/** Dashboard Summary Card — collapsible card showing the AI-generated dashboard summary */
const DashboardSummaryCard: React.FC<{
	summaryText: string;
	collapsedMaxLines?: number;
}> = ({ summaryText, collapsedMaxLines = 4 }) => {
	const [isExpanded, setIsExpanded] = useState(false);
	const lines = summaryText.split(/[.!?]\s+/).filter((s) => s.trim());
	const shouldCollapse = lines.length > collapsedMaxLines;

	const displayText =
		shouldCollapse && !isExpanded
			? lines.slice(0, collapsedMaxLines).join('. ') + '.'
			: summaryText;

	return (
		<div className="dashboard-summary-card">
			<div className="dashboard-summary-card__label">Dashboard Overview</div>
			<div className="dashboard-summary-card__text">{displayText}</div>
			{shouldCollapse && (
				<button
					className="insights-read-more-btn"
					onClick={() => setIsExpanded(!isExpanded)}
				>
					{isExpanded ? 'Show less' : 'Read more'}
				</button>
			)}
		</div>
	);
};

/** Chart Highlight Card — shows a chart's AI highlight with an Analyze button */
const ChartHighlightCard: React.FC<{
	widgetName: string;
	highlightText?: string;
	isLoading: boolean;
	isAnalyzed: boolean;
	isAnalyzing: boolean;
	onAnalyze: () => void;
	insightsData?: InsightsActionsResponse;
	onMouseEnter?: () => void;
	onMouseLeave?: () => void;
	showAnalyze?: boolean;
}> = ({
	widgetName,
	highlightText,
	isLoading,
	isAnalyzed,
	isAnalyzing,
	onAnalyze,
	insightsData,
	onMouseEnter,
	onMouseLeave,
	showAnalyze = true,
}) => {
	return (
		<div
			className="chart-highlight-card"
			onMouseEnter={onMouseEnter}
			onMouseLeave={onMouseLeave}
		>
			<div className="chart-highlight-card__header">
				<div className="chart-highlight-card__name">{widgetName}</div>
			</div>
			{isLoading ? (
				<div className="chart-highlight-card__loading">
					<Skeleton />
					<span className="spacer-xxs"></span>
					<Skeleton width="80%" />
				</div>
			) : (
				<>
					{highlightText && (
						<div className="chart-highlight-card__text">{highlightText}</div>
					)}
					{showAnalyze && !isAnalyzed && !isAnalyzing && (
						<Button
							variant="link"
							className="chart-highlight-card__analyze-btn"
							onClick={onAnalyze}
						>
							Analyze
						</Button>
					)}
					{isAnalyzing && (
						<div className="chart-highlight-card__analyzing">
							<Skeleton width="90%" />
							<span className="spacer-xxs"></span>
							<Skeleton width="70%" />
						</div>
					)}
					{showAnalyze && isAnalyzed && insightsData && (
						<div className="chart-highlight-card__insights">
							<InsightsActions
								insights={insightsData}
								hasBackground={false}
								collapsible={true}
								collapsedMaxItems={3}
							/>
						</div>
					)}
				</>
			)}
		</div>
	);
};

interface InsightsPanelProps {
	widgets: Widget[];
	insightsActions?: { entityId: number; insightsResponse: InsightsActionsResponse }[];
	setInsightsActions: React.Dispatch<
		React.SetStateAction<{ entityId: number; insightsResponse: InsightsActionsResponse }[]>
	>;
	isInsightsOpen: boolean;
	setIsInsightsOpen: (open: boolean) => void;
	openDashboardInsightsPanel: (open: boolean) => void;
	insightsEntities: TDashboardInightsEntity[];
	dashboard: TDashboard;
	setLambdaGraphHighlights: React.Dispatch<React.SetStateAction<LambdaGraphSummariesResponse[]>>;
	lambdaGraphHighlights: LambdaGraphSummariesResponse[];
	setDashboardSummaryText: React.Dispatch<React.SetStateAction<string | undefined>>;
	dashboardSummaryText: string | undefined;
	beginInsightsForWidget: (widget: Widget) => void;
	// ADDED: gap-fills widgets that never reported into currentGraphSummaryStats
	// (e.g. charts still deferred/below the fold, or skipped by ChartView's
	// internal loading/caching paths) by fetching their data directly. Returns
	// the newly-created StatSummary entries directly, since currentGraphSummaryStats
	// (shared context state) is not guaranteed to have re-rendered with them yet
	// by the time this promise resolves.
	fillMissingWidgetStats: (missingWidgets: Widget[]) => Promise<StatSummary[]>;
	onHighlightWidget?: (widget: Widget | undefined) => void;
	chatPrefillText?: string;
	// The dashboard's real view state (date range + applied filters), sent to
	// the summary lambdas and folded into the chat context.
	insightsFilterContext?: InsightsFilterContext;
}

const InsightsDashboardPanel: React.FC<InsightsPanelProps> = ({
	widgets,
	insightsActions,
	setInsightsActions,
	isInsightsOpen,
	setIsInsightsOpen,
	openDashboardInsightsPanel,
	insightsEntities,
	dashboard,
	setLambdaGraphHighlights,
	lambdaGraphHighlights,
	setDashboardSummaryText,
	dashboardSummaryText,
	beginInsightsForWidget,
	fillMissingWidgetStats, // ADDED
	onHighlightWidget,
	chatPrefillText,
	insightsFilterContext,
}) => {
	const [showDashboardSummaryInsights, setShowDashboardSummaryInsights] =
		useState<boolean>(false);
	const [isLoadingCalculationInsights, setIsLoadingCalculationInsights] =
		useState<boolean>(false);
	const [insightsFetchError, setInsightsFetchError] = useState(false);
	const [announcement, setAnnouncement] = useState('');
	const { currentGraphSummaryStats, dashboardChangeEvents, setDashboardChangeEvents } =
		useCommonStoreContext();
	const { zoneSettings, user } = useApplication();
	const isGranted = useIsGranted();
	const hasViewQuestionPerm = isGranted(Permission.ViewQuestion);
	const [isDiscoverQuestionsModalOpen, setIsDiscoverQuestionsModalOpen] = useState(false);
	const { dashboardId } = useParams();

	const permissionsSet = useMemo(() => new Set(user.permissions || []), [user.permissions]);
	const aiPermKeysPresent = (user.permissions || []).some(
		(p) => typeof p === 'string' && p.startsWith('canUseAI')
	);
	const hasAISummariesPerm = aiPermKeysPresent
		? permissionsSet.has(Permission.CanUseAISummaries)
		: true;
	const hasAIChatPerm = aiPermKeysPresent ? permissionsSet.has(Permission.CanUseAIChat) : true;

	// Track which widgets are being analyzed or have been analyzed
	const [analyzingWidgetIds, setAnalyzingWidgetIds] = useState<Set<number>>(new Set());
	const [analyzedWidgetIds, setAnalyzedWidgetIds] = useState<Set<number>>(new Set());

	// Chat state for shared thread
	const [chatMessages, setChatMessages] = useState<Message[]>([]);
	const [isChatThinking, setIsChatThinking] = useState(false);
	const [isInsightsModalOpen, setIsInsightsModalOpen] = useState(false);
	const [hasInteracted, setHasInteracted] = useState(false);
	const [refreshKey, setRefreshKey] = useState(0);
	const threadRef = useRef<HTMLDivElement | null>(null);
	const sidebarContainerRef = useRef<HTMLDivElement | null>(null);
	const isNearBottomRef = useRef(true);

	// Fallback debounce timer: if not all widgets have reported into
	// currentGraphSummaryStats within FALLBACK_DEBOUNCE_MS of the last arrival,
	// gap-fill the missing ones directly and proceed rather than waiting
	// indefinitely. Covers charts that legitimately have no data for the
	// current filters (ChartView/TableView only report when the retrieval
	// returned rows) or that never loaded at all (still below the fold and
	// untriggered by AutoWidget's IntersectionObserver).
	const FALLBACK_DEBOUNCE_MS = 5000; // tune based on observed real-world chart load times
	const fallbackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	const clearFallbackTimer = () => {
		if (fallbackTimerRef.current) {
			clearTimeout(fallbackTimerRef.current);
			fallbackTimerRef.current = null;
		}
	};

	// Dynamic sidebar height: measure panel position and adjust to fill viewport
	useEffect(() => {
		const sidebarEl = sidebarContainerRef.current;
		if (!sidebarEl) return;

		const panelEl = sidebarEl.closest('.right-sidebar-panel') as HTMLElement;
		if (!panelEl) return;

		const scrollContainer = panelEl.closest('.pf-c-page__main') || document.documentElement;

		let rafId: number | null = null;

		const updateHeight = () => {
			const panelRect = panelEl.getBoundingClientRect();
			const footer = document.querySelector('.ribbon-footer');
			const footerTop = footer ? footer.getBoundingClientRect().top : window.innerHeight;

			const bottom = Math.min(window.innerHeight, footerTop);
			const newHeight = Math.floor(Math.max(bottom - panelRect.top, 200));

			const currentHeight = parseInt(panelEl.style.height || '0', 10);
			if (Math.abs(newHeight - currentHeight) > 1) {
				panelEl.style.height = `${newHeight}px`;
				panelEl.style.maxHeight = `${newHeight}px`;
			}
		};

		const onScrollOrResize = () => {
			if (rafId !== null) return;
			rafId = requestAnimationFrame(() => {
				updateHeight();
				rafId = null;
			});
		};

		scrollContainer.addEventListener('scroll', onScrollOrResize, { passive: true });
		window.addEventListener('resize', onScrollOrResize, { passive: true });

		const subheader = document.querySelector('.sub-nav');
		let observer: ResizeObserver | null = null;
		if (subheader) {
			observer = new ResizeObserver(onScrollOrResize);
			observer.observe(subheader);
		}

		updateHeight();

		return () => {
			scrollContainer.removeEventListener('scroll', onScrollOrResize);
			window.removeEventListener('resize', onScrollOrResize);
			observer?.disconnect();
			if (rafId !== null) cancelAnimationFrame(rafId);
			panelEl.style.height = '';
			panelEl.style.maxHeight = '';
		};
	}, []);

	// Floating nav state
	const [showJumpToLatest, setShowJumpToLatest] = useState(false);
	const [showJumpToOverview, setShowJumpToOverview] = useState(false);
	const overviewRef = useRef<HTMLDivElement | null>(null);

	// Build cumulative chat context
	const buildChatContext = (): string => {
		const parts: string[] = [];

		// Dashboard identity
		if (dashboard?.name) {
			parts.push(`Dashboard: ${dashboard.name}`);
		}

		// Current view state — the date window and filters the user is looking at
		const viewParts: string[] = [];
		if (insightsFilterContext?.date_range) {
			const { start_date, end_date, period } = insightsFilterContext.date_range;
			viewParts.push(`Date range: ${start_date} to ${end_date} (${period})`);
		}
		if (insightsFilterContext?.dashboard_filters?.length) {
			viewParts.push(`Active filters: ${insightsFilterContext.dashboard_filters.join('; ')}`);
		}
		if (viewParts.length > 0) {
			parts.push(`Current View:\n${viewParts.join('\n')}`);
		}

		// Dashboard summary
		if (dashboardSummaryText) {
			parts.push(`Dashboard Summary:\n${dashboardSummaryText}`);
		}

		// Pending changes — any summary/highlights above were generated before
		// these, but the graph statistics below already reflect them
		if (dashboardChangeEvents.length > 0) {
			const changeLines = dashboardChangeEvents
				.map((e) => `${e.summary}: ${e.detail}`)
				.join('\n- ');
			parts.push(
				dashboardSummaryText
					? `Changes since the dashboard summary and highlights were generated (the graph statistics below already reflect them):\n- ${changeLines}`
					: `Recent dashboard changes (the graph statistics below already reflect them):\n- ${changeLines}`
			);
		}

		// All chart highlights
		if (lambdaGraphHighlights?.length > 0) {
			const highlightParts = lambdaGraphHighlights.map((h) => {
				const widget = insightWidgets.find((w) => widgetGraphId(w) === h.graphId);
				const name = widget ? getNameFromWidget(widget) : h.graphId;
				return `${name}: ${h.highlight}`;
			});
			parts.push(`Chart Highlights:\n${highlightParts.join('\n')}`);
		}

		// Stats for all charts
		if (currentGraphSummaryStats?.length > 0) {
			const statParts = currentGraphSummaryStats.map(
				(s) =>
					`${s.entityName}: mean=${s.mean}, total=${s.total}, min=${s.overallMin}, max=${s.overallMax}`
			);
			parts.push(`Graph Statistics:\n${statParts.join('\n')}`);
		}

		// Insights for all analyzed charts
		if (insightsActions && insightsActions.length > 0) {
			insightsActions.forEach(({ entityId, insightsResponse }) => {
				if (
					insightsResponse?.insights?.length > 0 ||
					insightsResponse?.actions?.length > 0
				) {
					const widget = insightWidgets.find(
						(w) => w.chart === entityId || w.report === entityId
					);
					const name = widget ? getNameFromWidget(widget) : `Entity ${entityId}`;
					if (insightsResponse.insights?.length > 0) {
						parts.push(
							`Insights for ${name}:\n- ${insightsResponse.insights.join('\n- ')}`
						);
					}
					if (insightsResponse.actions?.length > 0) {
						parts.push(
							`Actions for ${name}:\n- ${insightsResponse.actions.join('\n- ')}`
						);
					}
				}
			});
		}

		return parts.join('\n\n');
	};

	// Track when insights arrive for analyzed widgets
	useEffect(() => {
		if (!insightsActions) return;

		insightsActions.forEach(({ entityId, insightsResponse }) => {
			if (insightsResponse?.actions?.length > 0 || insightsResponse?.insights?.length > 0) {
				setAnalyzingWidgetIds((prev) => {
					const next = new Set(prev);
					next.delete(entityId);
					return next;
				});
				setAnalyzedWidgetIds((prev) => new Set([...prev, entityId]));
			}
		});
	}, [insightsActions]);

	// Tracks whether fetchDashboardInsights's network calls are actually in flight,
	// distinct from isLoadingCalculationInsights (which also covers the "waiting for
	// stragglers" UI state). Using a ref here — not state — so checking it doesn't
	// require it to be a dependency, and the auto-fetch effect can still react to
	// every new stat arrival while waiting, resetting the debounce timer each time.
	const isFetchInFlightRef = useRef(false);

	// Fetch dashboard summary + graph highlights.
	// Accepts an optional statsOverride — used when the fallback timer has just
	// gap-filled missing widgets via fillMissingWidgetStats and needs to send that
	// data immediately, since currentGraphSummaryStats (context state) is not
	// guaranteed to have re-rendered with the new entries by this point yet.
	const fetchDashboardInsights = (statsOverride?: StatSummary[]) => {
		// An in-flight retrieval from the previous dashboard can land after the
		// navigation reset — never send a stat for a widget that is not on this
		// dashboard.
		const currentWidgetGraphIds = new Set(
			insightWidgets.flatMap((w) => widgetGraphId(w) ?? [])
		);
		const statsToSend = (statsOverride ?? currentGraphSummaryStats).filter((s) =>
			currentWidgetGraphIds.has(s.graphId)
		);
		if (statsToSend.length === 0) return;
		if (isFetchInFlightRef.current) return;
		isFetchInFlightRef.current = true;

		setShowDashboardSummaryInsights(false);
		setIsLoadingCalculationInsights(true);
		setInsightsFetchError(false);

		// Snapshot the change ids now — they are consumed on success, and any
		// change arriving mid-generation must survive.
		const consumedChangeIds = new Set(dashboardChangeEvents.map((e) => e.id));
		const filtersToSend: InsightsFilterContext = {
			...insightsFilterContext,
			...(dashboardChangeEvents.length > 0 && {
				recent_changes: dashboardChangeEvents.map((e) => `${e.summary}: ${e.detail}`),
			}),
		};

		const request: LambdaGraphSummariesRequest = {
			dashboardId: dashboard.id?.toString() ?? '0',
			dashboardTitle: dashboard.name,
			filters: filtersToSend,
			graphPayloads: statsToSend,
			metadata: { extended_prompt: getExtendedPrompt(zoneSettings) },
		};

		retryWithBackoff(() => InsightsActionsAPI.GetGraphSummariesInsights(request), 2, 500)
			.then((response) => {
				setLambdaGraphHighlights(response.data);

				const dashboardSummaryRequest: LambdaDashboardSummaryRequest = {
					dashboardId: dashboard.id?.toString() ?? '0',
					dashboardTitle: dashboard.name,
					filters: filtersToSend,
					highlights: response.data,
					metadata: { extended_prompt: getExtendedPrompt(zoneSettings) },
				};

				return retryWithBackoff(
					() => InsightsActionsAPI.GetDashboardSummaryInsights(dashboardSummaryRequest),
					3,
					1000
				);
			})
			.then((response) => {
				setDashboardSummaryText(response.data.dashboardSummary);
				setShowDashboardSummaryInsights(true);
				// The freshly generated insights now reflect these changes — retire
				// their notices so the thread and chat context stay in sync.
				setDashboardChangeEvents((prev) =>
					prev.filter((e) => !consumedChangeIds.has(e.id))
				);
			})
			.catch((error: AxiosError) => {
				console.error('Insights fetch error after retries:', {
					message: error.message,
					code: error.code,
					status: error.response?.status,
					data: error.response?.data,
					isNetworkError: !error.response,
				});
				setInsightsFetchError(true);
				setShowDashboardSummaryInsights(true);
			})
			.finally(() => {
				setIsLoadingCalculationInsights(false);
				isFetchInFlightRef.current = false;
			});
	};

	// insightWidgets hoisted here (single declaration used throughout the component)
	const insightWidgets = widgets.filter(
		(x) => x.widget_type === 'chart' || x.widget_type == 'pivot_table'
	);

	// Shared by both the auto-fetch effect and the manual Refresh button.
	// Fires immediately once all widgets have reported in; otherwise shows the
	// loading state and waits up to FALLBACK_DEBOUNCE_MS for stragglers. If some
	// widgets still haven't reported in once that window elapses, actively
	// gap-fills them via fillMissingWidgetStats (fetching their data directly,
	// independent of AutoWidget/ChartView's render/visibility state) before
	// proceeding to fetchDashboardInsights — so charts that never loaded (e.g.
	// still below the fold) are still represented in the final payload.
	const attemptFetchInsights = () => {
		clearFallbackTimer();

		const expectedCount = insightWidgets.length;
		if (currentGraphSummaryStats.length >= expectedCount) {
			fetchDashboardInsights();
			return;
		}

		// Not all widgets have reported in — show loading state and wait.
		setShowDashboardSummaryInsights(false);
		setIsLoadingCalculationInsights(true);

		fallbackTimerRef.current = setTimeout(() => {
			// Snapshot at the moment the timer fires — this is what gets merged
			// with the gap-fill results below, since currentGraphSummaryStats
			// (context state) may not have re-rendered with any newer arrivals
			// by the time fillMissingWidgetStats resolves.
			const statsSnapshot = currentGraphSummaryStats;
			const reportedGraphIds = new Set(statsSnapshot.map((s) => s.graphId));
			const missingWidgets = insightWidgets.filter((w) => {
				const graphId = widgetGraphId(w);
				return graphId && !reportedGraphIds.has(graphId);
			});

			if (missingWidgets.length > 0) {
				fillMissingWidgetStats(missingWidgets)
					.then((newlyFilledStats) => {
						// Merge locally rather than trusting currentGraphSummaryStats
						// to have picked up the gap-filled entries by this point.
						const mergedStats = [...statsSnapshot, ...newlyFilledStats];
						fetchDashboardInsights(mergedStats);
					})
					.catch((error) => {
						console.error('Error filling missing widget stats:', error);
						fetchDashboardInsights(); // fall back to whatever's actually in state
					});
			} else {
				fetchDashboardInsights();
			}
		}, FALLBACK_DEBOUNCE_MS);
	};

	// Auto-fetch on stats change
	useEffect(() => {
		if (currentGraphSummaryStats.length === 0) return;
		if (isFetchInFlightRef.current) return;

		if (dashboardSummaryText) {
			setShowDashboardSummaryInsights(true);
			setIsLoadingCalculationInsights(false);
			return;
		}

		attemptFetchInsights();
	}, [currentGraphSummaryStats]);

	// Clean up any pending timer on unmount
	useEffect(() => {
		return () => clearFallbackTimer();
	}, []);

	useEffect(() => {
		if (isInsightsOpen && dashboardSummaryText) {
			setShowDashboardSummaryInsights(true);
		}
		if (isInsightsOpen && lambdaGraphHighlights && lambdaGraphHighlights.length > 0) {
			setIsLoadingCalculationInsights(false);
		}
	}, [isInsightsOpen, dashboardSummaryText, lambdaGraphHighlights]);

	useEffect(() => {
		if (!isLoadingCalculationInsights && showDashboardSummaryInsights) {
			// Clear the live region first
			setAnnouncement('');
			// Delay slightly so the screen reader detects it as a change
			setTimeout(() => {
				setAnnouncement('Dashboard insight summary and graph summaries have loaded.');
			}, 50);
		}
	}, [isLoadingCalculationInsights, showDashboardSummaryInsights]);

	// Scroll tracking for floating nav
	useEffect(() => {
		const container = threadRef.current;
		if (!container) return;

		const handleScroll = () => {
			const { scrollTop, scrollHeight, clientHeight } = container;
			const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
			const isNearTop = scrollTop < 100;

			isNearBottomRef.current = isNearBottom;

			setShowJumpToLatest(!isNearBottom && chatMessages.length > 0);
			setShowJumpToOverview(!isNearTop && scrollTop > 200);
		};

		container.addEventListener('scroll', handleScroll);
		return () => container.removeEventListener('scroll', handleScroll);
	}, [chatMessages.length]);

	// Auto-scroll when near bottom or when the user just sent a message (isChatThinking)
	useEffect(() => {
		if (chatMessages.length > 0 || isChatThinking) {
			if (isNearBottomRef.current || isChatThinking) {
				requestAnimationFrame(() => {
					requestAnimationFrame(() => {
						if (threadRef.current) {
							threadRef.current.scrollTop = threadRef.current.scrollHeight;
						}
					});
				});
			}
		}
	}, [chatMessages, isChatThinking]);

	const scrollToBottom = () => {
		threadRef.current?.scrollTo({ top: threadRef.current.scrollHeight, behavior: 'smooth' });
	};

	const scrollToOverview = () => {
		overviewRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
	};

	const getNameFromWidget = (widget: Widget) => {
		if (widget.widget_type == 'chart') {
			return insightsEntities.find((x) => x.chart?.id == widget.chart)?.chart?.name ?? '';
		}
		if (widget.widget_type == 'pivot_table') {
			return (
				insightsEntities.find(
					(x) => x.report?.id == widget.report || x.table?.id == widget.report
				)?.report?.name ?? ''
			);
		}
		return '';
	};

	const getHighlightForWidget = (widget: Widget): string | undefined => {
		if (!lambdaGraphHighlights || lambdaGraphHighlights.length === 0) return undefined;
		const graphId = widgetGraphId(widget);
		return lambdaGraphHighlights.find((x) => x.graphId == graphId)?.highlight;
	};

	const getInsightsForWidget = (widget: Widget): InsightsActionsResponse | undefined => {
		if (!insightsActions) return undefined;
		const entityId = widget.chart ?? widget.report ?? 0;
		const found = insightsActions.find((x) => x.entityId === entityId);
		return found?.insightsResponse;
	};

	const handleAnalyze = (widget: Widget) => {
		const entityId = widget.chart ?? widget.report ?? 0;
		setAnalyzingWidgetIds((prev) => new Set([...prev, entityId]));
		beginInsightsForWidget(widget);
	};

	const closeSidebar = () => {
		clearFallbackTimer();
		setIsInsightsOpen(false);
		openDashboardInsightsPanel(false);
		setInsightsActions([]);
		setAnalyzedWidgetIds(new Set());
		setAnalyzingWidgetIds(new Set());
		setChatMessages([]);
	};

	// When prefill text arrives from Discover, mark as interacted immediately
	useEffect(() => {
		if (chatPrefillText) {
			setHasInteracted(true);
		}
	}, [chatPrefillText]);

	const { noticesAnchoredAt, resetAnchors } = useNoticeAnchors(
		dashboardChangeEvents,
		chatMessages.length
	);

	const chatContext = useMemo(
		() => buildChatContext(),
		[
			dashboard?.name,
			dashboardSummaryText,
			lambdaGraphHighlights,
			currentGraphSummaryStats,
			insightsActions,
			dashboardChangeEvents,
			insightsFilterContext,
		]
	);

	const handleRefresh = () => {
		clearFallbackTimer();
		setDashboardSummaryText(undefined);
		setLambdaGraphHighlights([]);
		setInsightsActions([]);
		setChatMessages([]);
		// The thread is emptying — surviving notices re-anchor to the top rather
		// than to positions in the discarded conversation.
		resetAnchors();
		setAnalyzedWidgetIds(new Set());
		setAnalyzingWidgetIds(new Set());
		setShowDashboardSummaryInsights(false);
		setHasInteracted(false);
		setRefreshKey((k) => k + 1);
		attemptFetchInsights();
	};

	return (
		<div
			className="insights-sidebar-redesign"
			ref={sidebarContainerRef}
			role="complementary"
			aria-label="AI Insights"
		>
			{/* Sticky Header */}
			<div className="insights-sticky-header">
				<div className="insights-sticky-header__left">
					<FontAwesomeIcon
						icon={faSparkles}
						color="#0073c3"
					/>
					<span className="insights-sticky-header__title">AI * Insights</span>
				</div>
				<div className="insights-sticky-header__right">
					<Tooltip content="Refresh Insights">
						<Button
							variant="plain"
							className="insights-header-btn"
							onClick={handleRefresh}
						>
							<FontAwesomeIcon icon={faArrowsRotate} />
						</Button>
					</Tooltip>
					{hasViewQuestionPerm && (
						<Tooltip content="Discover Questions">
							<Button
								variant="plain"
								className="insights-header-btn"
								onClick={() => setIsDiscoverQuestionsModalOpen(true)}
							>
								<FontAwesomeIcon icon={faCompass} />
							</Button>
						</Tooltip>
					)}
					<Tooltip content="Expand">
						<Button
							variant="plain"
							className="insights-header-btn"
							onClick={() => setIsInsightsModalOpen(true)}
						>
							<FontAwesomeIcon icon={faExpand} />
						</Button>
					</Tooltip>
					<Tooltip content="Close">
						<Button
							variant="plain"
							className="insights-header-btn"
							onClick={closeSidebar}
						>
							<FontAwesomeIcon icon={faTimes} />
						</Button>
					</Tooltip>
				</div>
			</div>

			{/* Scrollable Thread */}
			<div
				className="insights-thread"
				ref={threadRef}
				tabIndex={0}
			>
				{/* Dashboard Summary */}
				<div ref={overviewRef}>
					{hasAISummariesPerm && !showDashboardSummaryInsights && (
						<div className="insights-loading">
							<Skeleton />
							<span className="spacer-xxs"></span>
							<Skeleton />
							<span className="spacer-xxs"></span>
							<Skeleton width="60%" />
						</div>
					)}
					{hasAISummariesPerm && showDashboardSummaryInsights && dashboardSummaryText && (
						<DashboardSummaryCard summaryText={dashboardSummaryText} />
					)}
					{showDashboardSummaryInsights &&
						!dashboardSummaryText &&
						insightsFetchError && (
							<div style={{ padding: '1rem', textAlign: 'center' }}>
								<p style={{ color: '#6a6e73', marginBottom: '0.5rem' }}>
									Failed to generate insights.
								</p>
								<Button
									variant="link"
									onClick={() => {
										fetchDashboardInsights();
									}}
								>
									Try again
								</Button>
							</div>
						)}
				</div>

				{/* Chart Highlight Cards */}
				{hasAISummariesPerm &&
					insightWidgets.map((widget) => {
						const entityId = widget.chart ?? widget.report ?? 0;
						const widgetName = getNameFromWidget(widget);
						const highlightText = getHighlightForWidget(widget);
						const isAnalyzing = analyzingWidgetIds.has(entityId);
						const isAnalyzed = analyzedWidgetIds.has(entityId);
						const widgetInsights = getInsightsForWidget(widget);

						return (
							<ChartHighlightCard
								key={widget.id}
								widgetName={widgetName}
								highlightText={highlightText}
								isLoading={isLoadingCalculationInsights}
								isAnalyzed={isAnalyzed}
								isAnalyzing={isAnalyzing}
								onAnalyze={() => handleAnalyze(widget)}
								insightsData={widgetInsights}
								onMouseEnter={() => onHighlightWidget?.(widget)}
								onMouseLeave={() => onHighlightWidget?.(undefined)}
							/>
						);
					})}

				{/* Chat messages in shared thread, with muted change notices
				    interleaved where the dashboard's data changed */}
				{((hasAIChatPerm && chatMessages.length > 0) ||
					((hasAISummariesPerm || hasAIChatPerm) &&
						dashboardChangeEvents.length > 0)) && (
					<div className="insights-chat-thread">
						{chatMessages.map((msg, idx) => (
							<React.Fragment key={`msg-${msg.id}`}>
								{noticesAnchoredAt(idx).map((ev) => (
									<InsightsChangeNotice
										key={`chg-${ev.id}`}
										event={ev}
										subject="dashboard"
									/>
								))}
								<ChatMessages
									messages={[msg]}
									isThinking={false}
								/>
							</React.Fragment>
						))}
						{noticesAnchoredAt(chatMessages.length).map((ev) => (
							<InsightsChangeNotice
								key={`chg-${ev.id}`}
								event={ev}
								subject="dashboard"
							/>
						))}
						{isChatThinking && (
							<ChatMessages
								messages={[]}
								isThinking={true}
							/>
						)}
					</div>
				)}

				{/* Floating nav buttons */}
				{showJumpToOverview && (
					<button
						className="floating-nav-btn floating-nav-btn--top"
						onClick={scrollToOverview}
					>
						<FontAwesomeIcon icon={faChevronUp} /> Overview
					</button>
				)}
				{showJumpToLatest && (
					<button
						className="floating-nav-btn floating-nav-btn--bottom"
						onClick={scrollToBottom}
					>
						<FontAwesomeIcon icon={faChevronDown} /> Latest
					</button>
				)}
			</div>

			{/* Hidden live region for announcements */}
			<div
				aria-live="polite"
				style={{
					position: 'absolute',
					left: '-9999px',
					height: '1px',
					width: '1px',
					overflow: 'hidden',
				}}
			>
				{announcement}
			</div>

			{/* Pinned Chat Input */}
			<div className="insights-pinned-input">
				{chatMessages.length === 0 && !hasInteracted && (
					<small className="insights-chat-prompt">Ask a follow-up question</small>
				)}
				{/* Keyed on refreshKey so Refresh remounts the chat: ChatInterface owns
				    the message list that is actually sent to the model, and clearing
				    our chatMessages mirror does not reach it. The remount also resets
				    its already-auto-submitted flag, hence the first-mount guard on
				    autoSubmitMessage below. */}
				<ChatInterface
					key={refreshKey}
					context={chatContext}
					externalThread={true}
					onMessagesChange={setChatMessages}
					onThinkingChange={setIsChatThinking}
					showModal={isInsightsModalOpen}
					onModalClose={() => setIsInsightsModalOpen(false)}
					disabled={!showDashboardSummaryInsights}
					onFirstInteraction={() => setHasInteracted(true)}
					autoSubmitMessage={refreshKey === 0 ? chatPrefillText : undefined}
					modalHeaderContent={
						dashboardSummaryText ? (
							<DashboardSummaryCard summaryText={dashboardSummaryText} />
						) : undefined
					}
				/>
			</div>

			<DiscoverQuestionsModal
				isOpen={isDiscoverQuestionsModalOpen}
				onClose={() => setIsDiscoverQuestionsModalOpen(false)}
				dashboardId={dashboardId ? parseInt(dashboardId) : undefined}
			/>
		</div>
	);
};

export default InsightsDashboardPanel;
